import { memo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Button, Stack, Paper, Alert } from "@mui/material";
import PhotoCapture from "../../components/PhotoCapture";
import LoadingComponent from "../../components/LoadingComponent";
import { useAuth } from "../../hooks/useAuth";
import { useDriverDeliveriesDetail } from "../../hooks/useDriverDeliveryDetail";
import { useDeliveryItems } from "../../hooks/useDeliveryItems";
import { mapStatus, getStatusColor } from "../../utils/functions";
import DeliveryItems from "../../components/DeliveryItems";

const mainButtonStyles = {
  py: 1.5,
  fontWeight: "bold",
  fontSize: "1.1rem",
  borderRadius: "0px !important",
  border: "0px !important",
};

const RouteDetail = () => {
  const { deliveryId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const {
    delivery,
    loading,
    error,
    formState,
    imageAllowedStatus,
    handleFormChange,
    updateDelivery,
    uploadDeliveryEvidence,
    whatsapp,
  } = useDriverDeliveriesDetail(deliveryId);

  const {
    refetchDeliveryItems,
    deliveryItems,
    deliveryItemsCount,
    pagination,
    handleChangePage,
    handleChangeRowsPerPage,
    createDeliveryItem,
    deleteDeliveryItem,
    updateDeliveryItemStatus,
    loading: loadingDeliveryItems,
    error: errorDeliveryItem,
  } = useDeliveryItems();

  useEffect(() => {
    if (![null, undefined].includes(delivery?.id)) {
      refetchDeliveryItems(delivery.order_id);
    }
  }, [delivery]);

  return (
    <Box sx={{ p: 0.5, backgroundColor: "white" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="body1">Hola, {user.full_name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {user.role}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="error"
          size="small"
          sx={{
            ...mainButtonStyles,
            mb: 2,
            backgroundColor: "#ff6666 !important",
          }}
          onClick={logout}
        >
          Salir
        </Button>
      </Box>
      <Button
        variant="contained"
        color="error"
        fullWidth
        sx={{
          ...mainButtonStyles,
          mb: 2,
          backgroundColor: "#ff6666 !important",
        }}
        onClick={(event) => {
          navigate("/driver/my-routes");
        }}
      >
        VOLVER
      </Button>

      {loading ? (
        <LoadingComponent />
      ) : (
        <>
          {error ? (
            <Alert sx={{ borderRadius: "0px !important" }} severity="error">
              {error}
            </Alert>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              pl: 0,
              pt: 1,
              pb: 1,
              pr: 0,
              mb: 0,
              backgroundColor: "#fff",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bolder" }}>
              {delivery.orderId}
            </Typography>
            <Typography variant="body2">{delivery.created_at}</Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, fontWeight: "bolder !important" }}
            >
              ORDER: {delivery.order}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, fontWeight: "bolder !important" }}
            >
              CLIENTE: {delivery.client}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, fontWeight: "bolder !important" }}
            >
              Teléfono: {delivery.customer_phone}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, fontWeight: "bolder !important" }}
            >
              NOTAS: {delivery.notes}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                fontWeight: "bolder !important",
              }}
            >
              Estado General:
              <>
              <Typography
                variant="spam"
                sx={{
                  mt: 1,
                  m: 2,
                  padding: "5px",
                  fontWeight: "bolder !important",
                  backgroundColor: getStatusColor(delivery.status),
                  color: "#fff",
                  textAlign: "center",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                }}
              >
                {mapStatus(delivery.status)}
              </Typography>
              </>
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: "bolder !important", mt: 2 }}
            >
              {delivery.address}
            </Typography>
          </Paper>

          {delivery.photos && delivery.photos.length > 0 && (
            <Box
              sx={{
                mt: 1,
                mb: 2,
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 1, color: "text.secondary" }}
              >
                Evidencia fotográfica previa:
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  overflowX: "auto",
                  pb: 1,
                  "&::-webkit-scrollbar": { height: "6px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#ccc",
                    borderRadius: "4px",
                  },
                }}
              >
                {delivery.photos.map((photoUrl, index) => (
                  <Box
                    key={`historical-photo-${index}`}
                    component="a"
                    href={photoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ flexShrink: 0, textDecoration: "none" }}
                  >
                    <Box
                      component="img"
                      src={photoUrl}
                      alt={`Evidencia ${index + 1}`}
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 1,
                        objectFit: "cover",
                        border: "1px solid #e0e0e0",
                        boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Stack spacing={2}>
            <DeliveryItems
              delivery={delivery}
              deliveryItems={deliveryItems}
              deliveryItemsCount={deliveryItemsCount}
              pagination={pagination}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              createDeliveryItem={createDeliveryItem}
              deleteDeliveryItem={deleteDeliveryItem}
              updateDeliveryItemStatus={updateDeliveryItemStatus}
              loading={loadingDeliveryItems}
              error={errorDeliveryItem}
            />
            {imageAllowedStatus &&
              (imageAllowedStatus.includes(formState.status) ||
                delivery.photos.length >= 6) && (
                <PhotoCapture
                  onPhotosChange={(photos) =>
                    handleFormChange("photos", photos)
                  }
                  maxPhotos={2}
                  onUploadSubmit={uploadDeliveryEvidence}
                />
              )}

            <Button
              component="a"
              href={`https://wa.me/${
                delivery.customer_phone
              }?text=${encodeURIComponent(`Hola ${delivery.client}! Tenemos un pedido a nombre tuyo de la tienda ${delivery.seller} con ID ${delivery.order}, favor solicitamos que estés atento/a a la entrega.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              sx={{
                backgroundColor: "#25D366 !important",
                color: "#FFFFFF !important",
                fontWeight: "bolder !important",
                textTransform: "none",
                padding: "8px 16px",
                borderRadius: "0px !important",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1ebe5b",
                  boxShadow: "none",
                },
              }}
            >
              Comunicarse por whatsapp
            </Button>

            <Button
              variant="contained"
              color="success"
              fullWidth
              sx={{
                ...mainButtonStyles,
                mt: 2,
                minHeight: "4rem !important",
                fontWeight: "Bolder !important",
              }}
              onClick={updateDelivery}
            >
              FINALIZAR
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default memo(RouteDetail);
